import { Box } from '@mui/material';
import { neutral } from '@ahora-patagonia/design-tokens';
import type { NotaPublicaCard } from '@ahora-patagonia/contracts';
import { FilaNotaMiniatura } from '../../../shared/notas/FilaNotaMiniatura.js';

export interface PlantillaListaProps {
  notas: NotaPublicaCard[];
  /** Posición numerada al frente: la disposición con la que se dibuja "Más leídas". */
  numerada?: boolean;
  /**
   * Con qué número arranca la numeración. Sirve para el bloque paginado: la segunda página del
   * ranking empieza en 7 y no vuelve a 1, que sería decir que hay dos primeras.
   */
  inicio?: number;
  /** Tope de notas (4 en una columna; 6 cuando va a 2 columnas en escritorio). */
  maximo?: number;
}

/**
 * Plantilla C — lista con miniatura chica: 1 columna (96 px de foto en teléfono, 132 en
 * tablet) y 2 columnas en escritorio (handoff §1.2).
 *
 * Una página corta no necesita rellenos: en el bloque paginado todas las páginas conviven en el
 * riel, así que el alto lo fija la más larga y no salta al pasar de una a otra.
 *
 * Numerada se dibuja como **lista ordenada** (`<ol>`): el número visible va `aria-hidden`, así que sin
 * esto la posición —que en un ranking es la mitad de la información— no existiría para un lector de
 * pantalla. Sin numerar sigue siendo una lista suelta, porque ahí el orden no dice nada.
 */
export function PlantillaLista({
  notas,
  numerada = false,
  inicio = 1,
  maximo = 6,
}: PlantillaListaProps) {
  const visibles = notas.slice(0, maximo);
  return (
    <Box
      component={numerada ? 'ol' : 'div'}
      {...(numerada ? { start: inicio } : {})}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
        columnGap: 4,
        ...(numerada ? { listStyle: 'none', m: 0, p: 0 } : {}),
      }}
    >
      {visibles.map((nota, i) => (
        <Box
          key={nota.id}
          {...(numerada ? { component: 'li' as const } : {})}
          sx={{ py: 1.5, ...(i > 0 ? { borderTop: { xs: `1px solid ${neutral[300]}`, lg: i > 1 ? `1px solid ${neutral[300]}` : 'none' } } : {}) }}
        >
          <FilaNotaMiniatura nota={nota} {...(numerada ? { numero: inicio + i } : {})} />
        </Box>
      ))}
    </Box>
  );
}
